import { subscriptionApiEndpoint } from "constant/apiConstants";
import { useCommonContext } from "hooks‬/CommonContext";
import React, { FC, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import apiInterceptor from "services/apiInterceptor";
import Select from "react-select";
import { useSearchParams } from "react-router-dom";
import {
  BsFillPatchCheckFill,
  BsExclamationTriangleFill,
} from "react-icons/bs";
import { KTIcon } from "helpers";
import useFetch from "hooks‬/useFetch";
import Pagination from "components/base/Pagination";

interface PropsType {
  showModal: boolean;
  closeModal: () => void;
  fetchAPI: any;
  role: any;
}
const RoleDelete: FC<PropsType> = ({
  showModal,
  closeModal,
  fetchAPI,
  role,
}) => {
  const [searchParams] = useSearchParams();
  const productGuid = searchParams.get("product");
  const [isUserAssignedSuccessfully, setIsUserAssignedSuccessfully] =
    useState(false);
  const { showSuccessToast } = useCommonContext();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isAlreadyUserExist, setIsAlreadyUserExist] = React.useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});

  const {
    data: users,
    isFetchLoading,
    currentPage,
    totalPages,
    cursors,
    fetchData: fetchUserHandler,
  } = useFetch<any>({
    url: `${subscriptionApiEndpoint.ENDPOINT_USER_ROLE}/${role.guid}/users`,
    limit: 100,
    query: "status=A",
    isCalled: showModal,
  });

  const handleDelete = () => {
    setIsSubmitting(true);
    apiInterceptor
      .delete(`${subscriptionApiEndpoint.ENDPOINT_USER_ROLE}/${role.guid}`)
      .then((response) => {
        if (response?.data?.status === "ERROR") {
          setIsAlreadyUserExist(true);
        } else {
          showSuccessToast("Role deleted successfully");
          closeModal();
          fetchAPI();
        }
        setIsSubmitting(false);
      })
      .catch((error) => {
        setIsSubmitting(false);
      });
  };

  const [selectedRoles, setSelectedRoles] = useState<{
    [key: string]: { value: string; label: string } | null;
  }>({});

  const handleRoleChange = async (userId: string, selectedOption: any) => {
    setSelectedRoles((prev: any) => ({
      ...prev,
      [userId]: selectedOption,
    }));
  };

  const handleConfirmRessign = async () => {
    const errors: { [key: string]: string } = {};

    users.forEach((user) => {
      if (!selectedRoles?.[user.guid]?.value) {
        errors[user.guid] = "Please select a new role";
      }
    });

    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const reassignedData = Object.entries(selectedRoles).map(
      ([userGuid, role]) => ({
        userGuid,
        updatedRoleGuid: role?.value,
      })
    );

    try {
      await apiInterceptor.put(
        `${subscriptionApiEndpoint.ENDPOINT_USER_ROLE}/${role.guid}/users`,
        reassignedData
      );
      setIsUserAssignedSuccessfully(true);
      setIsAlreadyUserExist(false);
    } catch (err) {
      console.error("Error reassigning users:", err);
    }
  };

  const [rolesOption, setRolesOption] = useState<any[]>([]);
  const fetchRoleHnalder = async (productGuid: string) => {
    await apiInterceptor
      .get(
        `${subscriptionApiEndpoint.ENDPOINT_ALL_PRODUCTS}/${productGuid}/roles`
      )
      .then((response) => {
        setRolesOption(
          response.data.data.map((role: any) => ({
            value: role.guid,
            label: role.displayName,
          }))
        );
      })
      .catch((error) => console.error("error"));
  };

  useEffect(() => {
    if (isAlreadyUserExist) {
      fetchRoleHnalder(productGuid ?? "");
    }
  }, [isAlreadyUserExist, productGuid]);

  useEffect(() => {
    if (!showModal) {
      setIsUserAssignedSuccessfully(false);
      setIsAlreadyUserExist(false);
      setSelectedRoles({});
      setRolesOption([]);
    }
  }, [showModal]);

  return (
    <Modal
      show={showModal}
      onHide={closeModal}
      centered
      size={isAlreadyUserExist ? "lg" : "sm"}
      backdrop="static"
      dialogClassName="delete-user-modal"
    >
      {isAlreadyUserExist ? (
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title fs-2">Reassign Users to Another Role</h5>
            <button type="button" onClick={closeModal} className="btn-close" />
          </div>
          <div className="modal-body">
            <table className="table align-middle">
              <thead>
                <tr className="fs-4 text-gray-500">
                  <th>Users Name</th>
                  <th>Current Role</th>
                  <th>
                    <KTIcon iconName="arrow-right" className="fs-2" />
                  </th>
                  <th>New Role</th>
                </tr>
              </thead>
              <tbody>
                {users?.map((user) => (
                  <tr className="fs-4" key={user.guid}>
                    <td>
                      {user?.firstName} {user?.lastName}
                    </td>
                    <td>{role?.displayName}</td>
                    <td>
                      <KTIcon iconName="arrow-right" className="fs-2 " />
                    </td>
                    <td>
                      <Select
                        options={rolesOption}
                        value={selectedRoles[user.guid] ?? null}
                        onChange={(selectedOption) => {
                          handleRoleChange(user.guid, selectedOption);
                          setValidationErrors((prev) => ({
                            ...prev,
                            [user.guid]: "",
                          }));
                        }}
                        placeholder="Select New Role"
                        className="react-select-styled react-select-solid react-select-lg"
                        classNamePrefix="react-select"
                      />
                      {validationErrors[user.guid] && (
                        <div className="text-danger mt-1">
                          {validationErrors[user.guid]}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              cursors={cursors}
              currentPage={currentPage}
              totalPages={totalPages}
              isLoading={isFetchLoading}
              handlePageChange={fetchUserHandler}
            />
          </div>
          <div className="d-flex justify-content-end gap-4 p-6">
            <button className="btn btn-light" onClick={closeModal}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleConfirmRessign}>
              Confirm
            </button>
          </div>
        </div>
      ) : (
        <Modal.Body className="text-center p-7">
          <div className="mb-3">
            {isUserAssignedSuccessfully ? (
              <div
                className="rounded-circle mx-auto d-flex align-items-center justify-content-center mt-1"
                style={{
                  backgroundColor: "#CEF8C9",
                  width: "50px",
                  height: "50px",
                }}
              >
                <BsFillPatchCheckFill color="#22c55e" size={28} />
              </div>
            ) : (
              <div
                className="rounded-circle mx-auto d-flex align-items-center justify-content-center mt-1"
                style={{
                  backgroundColor: "#FFEAEA",
                  width: "50px",
                  height: "50px",
                }}
              >
                <BsExclamationTriangleFill color="#f43f5e" size={28} />
              </div>
            )}
          </div>
          <h4 className="fw-medium mx-10 my-6 ">
            {isUserAssignedSuccessfully
              ? "Users Reassigned Successfully"
              : "Are you sure you want to delete this Role?"}
          </h4>
          <div className="text-muted fs-4 mb-4">
            {isUserAssignedSuccessfully
              ? "All users have been moved to the new role. You can delete now."
              : role.displayName}
          </div>
          <div className="  row justify-content-between  ">
            <div className="col-6">
              <button className="btn btn-secondary w-100" onClick={closeModal}>
                Cancel
              </button>
            </div>

            <div className="col-6">
              <button
                className="btn btn-danger w-100"
                onClick={() => handleDelete()}
                disabled={isSubmitting}
              >
                Delete
              </button>
            </div>
          </div>
        </Modal.Body>
      )}
    </Modal>
  );
};

export default RoleDelete;
